import React, { memo, useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import urljoin from 'url-join';

import { formatCurrentHours, formatTargetHours } from 'Util/myPath';
import { Box, Flex, Text } from 'Components/Base';
import Loading from 'Components/Loading';
import { useConfig } from 'Context/ConfigContext';
import MyPathBanner from 'Routes/MyCourses/Components/MyPathBanner';
import TargetGoalCard from 'Routes/MyCourses/Components/TargetGoalCard';
import { getStartAndEndDateOfCurrentWeek } from 'Routes/MyCourses/Components/utils';
import WeeklyStackedBarChart from 'Routes/MyCourses/Components/WeeklyStackedBarChart';
import MyPathWeeklyProgress from 'Routes/MyCourses/Containers/MyPathWeeklyProgress/MyPathWeeklyProgress';
import useGetMyProgressTracker from 'Routes/MyCourses/Domain/useGetMyProgressTracker';
import useGetWeeklyLearningProgressWithCapWorkload from 'Routes/MyCourses/Domain/useGetWeeklyLearningProgressWithCapWorkload';
import useMPMyGoals from 'Routes/MyCourses/Domain/useMPMyGoals';
import useSyncMPMyProgressTracker from 'Routes/MyCourses/Domain/useSyncMPMyProgressTracker';
import { useMyPathTracking } from 'Util/Analytics/useMyPathTracking';
import { openUrl } from 'Util/Capacitors/SkooldioCapacitor';
import fetch2, { METHOD } from 'Util/fetch';
import useCompatibleDisplay from 'Util/Hooks/useCompatibleDisplay';
import { useDisclosure } from 'Util/Hooks';
import useGetPreviousMyProgressTracker from 'Routes/MyCourses/Domain/useGetPreviouseMyProgressTracker';

import SeeLastSemesterBlockProductsModal from '../MyPathMilestoneTracker/SeeLastSemesterBlockProductsModal';
import IncompatibleMyPathWithMobile from '../../Components/IncompatibleMyPathWithMobile';
import MyPathProgressTracker from '../MyPathProgressTracker';

const MyPathMilestoneTrackerWrapper = styled(Flex)(({ theme }) => ({
  marginTop: '24px',
  padding: '0 16px',
  columnGap: '16px',
  flexDirection: 'row',
  alignItems: 'stretch',
  height: '100%',
  gap: `${theme.space[4]}px`,
  '& > *:last-child': {
    flex: 1,
    minWidth: 0,
  },

  [`@media (max-width: ${theme._breakpoints[2]}px)`]: {
    flexDirection: 'column',
  },
}));

const MyPathWeeklyMilestoneTrackerWrapper = styled(Flex)(({ theme, haveGoals }) => ({
  width: '100%',
  gap: `${theme.space[4]}px`,
  [`@media (max-width: ${theme._breakpoints[2]}px)`]: {
    flexDirection: haveGoals ? 'row' : 'column',
  },
}));

const WeeklyStackedBarChartWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: theme.colors.white,
  borderRadius: '16px',
  padding: '12px',
  width: 'fit-content',
  [`@media (max-width: ${theme._breakpoints[2]}px)`]: {
    width: '100%',
  },
}));

const ResponsiveMyPathProgressTrackerWrapper = styled(Flex)(({ theme }) => ({
  backgroundColor: theme.colors.white,
  borderRadius: '16px',
  padding: '12px',
  minHeight: '400px',
}));

const LoadingStyled = styled(Flex)(({ theme }) => ({
  backgroundColor: theme.colors.white,
  borderRadius: '16px',
  width: '100%',
  minHeight: '364px',
  alignItems: 'center',
  justifyContent: 'center',
  [`@media (max-width: ${theme._breakpoints[2]}px)`]: {
    minHeight: '730px',
  },
}));

// eslint-disable-next-line react/display-name
const MyPathMilestoneTrackerSection = memo((props) => {
  const { ...restProps } = props;

  const { myPath, userApi } = useConfig();
  const { isLargeTablet } = useCompatibleDisplay();
  const [
    isOpenSeeLastSemesterBlockProductsModal,
    onOpenSeeLastSemesterBlockProductsModal,
    onCloseSeeLastSemesterBlockProductsModal,
  ] = useDisclosure();
  const { logOnboardingClick } = useMyPathTracking();

  const { startDate, endDate } = getStartAndEndDateOfCurrentWeek();

  const { data: mpMyGoals, loading: isLoadingMyGoals, refetch: refetchMyGoals } = useMPMyGoals();

  const {
    weeklyLearningData,
    studentSemestersData,
    loading: isLoadingWeeklyLearning,
    refetch: refetchWeeklyLearning,
  } = useGetWeeklyLearningProgressWithCapWorkload({
    startDate,
    endDate,
  });

  const {
    getMyProgressTracker,
    mpMyProgressTracker,
    loading: isLoadingMyProgressTracker,
  } = useGetMyProgressTracker();

  const {
    getPreviousMyProgressTracker,
    mpPreviousMyProgressTracker,
    loading: isLoadingPreviousMyProgressTracker,
  } = useGetPreviousMyProgressTracker();

  const {
    mutate: syncMPMyProductTimelineProgresses,
    loading: isLoadingSyncMyProductTimelineProgresses,
  } = useSyncMPMyProgressTracker();

  const haveGoals = useMemo(() => mpMyGoals?.length > 0, [mpMyGoals]);

  const handleSyncAndFetch = useCallback(async () => {
    if (haveGoals) {
      await syncMPMyProductTimelineProgresses();
      getMyProgressTracker();
      getPreviousMyProgressTracker();
    }
  }, [
    syncMPMyProductTimelineProgresses,
    getMyProgressTracker,
    getPreviousMyProgressTracker,
    haveGoals,
  ]);

  const handleRefetchAll = useCallback(async () => {
    await Promise.all([refetchMyGoals(), refetchWeeklyLearning(), handleSyncAndFetch()]);
  }, [refetchMyGoals, refetchWeeklyLearning, handleSyncAndFetch]);

  const isLoading = useMemo(
    () =>
      isLoadingMyGoals ||
      isLoadingWeeklyLearning ||
      isLoadingMyProgressTracker ||
      isLoadingPreviousMyProgressTracker ||
      isLoadingSyncMyProductTimelineProgresses,
    [
      isLoadingMyGoals,
      isLoadingWeeklyLearning,
      isLoadingMyProgressTracker,
      isLoadingPreviousMyProgressTracker,
      isLoadingSyncMyProductTimelineProgresses,
    ]
  );

  const goToMyPath = useCallback(async () => {
    const token = await fetch2(urljoin(userApi, '/me/session'), {}, METHOD.POST, null, {
      isRedirect: false,
      includeCredentials: true,
    });
    const myPathUrl = myPath.onboardingUrl.replace('{token}', token);
    logOnboardingClick();
    openUrl(myPathUrl, {
      defaultFunction: () => window.open(myPathUrl, '_blank'),
      onClose: () => {
        handleRefetchAll();
      },
    });
  }, [logOnboardingClick, myPath.onboardingUrl, userApi, handleRefetchAll]);

  const reportUrl = myPath.reportUrl ?? '';
  const goToMyPathReport = useCallback(() => {
    if (reportUrl) {
      openUrl(reportUrl, () => window.open(reportUrl, '_blank'));
    }
  }, [reportUrl]);

  const targetHours = formatTargetHours(
    studentSemestersData?.[0]?.weekdayCapWorkload ?? 0,
    studentSemestersData?.[0]?.weekendCapWorkload ?? 0
  );

  const currentHours = formatCurrentHours(
    weeklyLearningData?.reduce((total, item) => total + item.duration, 0) ?? 0
  );

  const hasPreviousSemester =
    mpPreviousMyProgressTracker?.studentSemester?.startDate &&
    mpPreviousMyProgressTracker?.studentSemester?.endDate;

  const renderContent = useCallback(() => {
    if (isLoading) {
      return (
        <LoadingStyled>
          <Loading true />
        </LoadingStyled>
      );
    }

    if (haveGoals) {
      return (
        <>
          <MyPathWeeklyMilestoneTrackerWrapper haveGoals>
            {/* TODO: checkin section */}
            <MyPathWeeklyProgress
              goToMyPath={goToMyPath}
              goToMyPathReport={goToMyPathReport}
              weeklyLearningProgresses={weeklyLearningData}
              studentSemesters={studentSemestersData}
              haveGoals={haveGoals}
              targetHours={targetHours}
              currentHours={currentHours}
              mpMyProgressTracker={mpMyProgressTracker}
              onOpenSeeLastSemesterBlockProductsModal={onOpenSeeLastSemesterBlockProductsModal}
              hasPreviousSemester={hasPreviousSemester}
            />
          </MyPathWeeklyMilestoneTrackerWrapper>
          {isLargeTablet ? (
            <ResponsiveMyPathProgressTrackerWrapper>
              <MyPathProgressTracker
                goToMyPath={goToMyPath}
                goToMyPathReport={goToMyPathReport}
                mpMyProgressTracker={mpMyProgressTracker}
                onOpenSeeLastSemesterBlockProductsModal={onOpenSeeLastSemesterBlockProductsModal}
                hasPreviousSemester={hasPreviousSemester}
              />
            </ResponsiveMyPathProgressTrackerWrapper>
          ) : null}
          {isOpenSeeLastSemesterBlockProductsModal && (
            <SeeLastSemesterBlockProductsModal
              isOpen={isOpenSeeLastSemesterBlockProductsModal}
              onCloseModal={onCloseSeeLastSemesterBlockProductsModal}
              mpPreviousMyProgressTracker={mpPreviousMyProgressTracker}
              onRefetch={handleSyncAndFetch}
            />
          )}
        </>
      );
    }

    return (
      <>
        {/* TODO: checkin section */}
        <WeeklyStackedBarChartWrapper>
          <Flex
            flexDirection={'column'}
            alignItems={'center'}
            justifyContent={'space-between'}
            gridRowGap={1}
            style={{ height: '100%' }}
          >
            <Flex
              flexDirection={'column'}
              gridRowGap={1}
              alignItems={'center'}
              justifyItems={'center'}
            >
              <Text textStyle={'subtitle1'} fontWeight={600} lineHeight={'28px'}>
                ชั่วโมงเรียนสัปดาห์นี้
              </Text>
              <WeeklyStackedBarChart
                data={weeklyLearningData}
                studentSemesters={studentSemestersData}
                haveGoals={haveGoals}
              />
            </Flex>
            <TargetGoalCard targetHours={targetHours} currentHours={currentHours} />
          </Flex>
        </WeeklyStackedBarChartWrapper>
        <MyPathBanner goToMyPath={goToMyPath} />
      </>
    );
  }, [
    isLoading,
    haveGoals,
    weeklyLearningData,
    studentSemestersData,
    targetHours,
    currentHours,
    goToMyPath,
    goToMyPathReport,
    mpMyProgressTracker,
    onOpenSeeLastSemesterBlockProductsModal,
    hasPreviousSemester,
    isLargeTablet,
    isOpenSeeLastSemesterBlockProductsModal,
    onCloseSeeLastSemesterBlockProductsModal,
    mpPreviousMyProgressTracker,
    handleSyncAndFetch,
  ]);

  useEffect(() => {
    handleSyncAndFetch();
  }, [handleSyncAndFetch]);

  return (
    <MyPathMilestoneTrackerWrapper {...restProps}>{renderContent()}</MyPathMilestoneTrackerWrapper>
  );
});

const MyPathMilestoneTracker = () => {
  const { isCompatibleMobile } = useCompatibleDisplay();

  if (isCompatibleMobile) {
    return (
      <Box px={2} mt={4}>
        <IncompatibleMyPathWithMobile />
      </Box>
    );
  }

  return <MyPathMilestoneTrackerSection />;
};

export default MyPathMilestoneTracker;
